import produce from "immer";
import { chunk, find, get, keyBy, omit, values } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import useSWR, { useSWRConfig } from "swr";
import { ApiResponse, fetchApi } from "../../utils/api";
import { Contact, ContactPayment, PaymentMethod } from "../../utils/types";
import { CurrencyKey } from "../../utils/useCurrency";
import usePrevious from "../../utils/usePrevious";
import { useComments } from "../comments";
import { useLabels } from "../labels";
import { useLoading } from "../loading";
import { LoadingKey } from "../loading/LoadingProvider";
import { useToast } from "../toast";
import { useWorkspaces } from "../workspaces";

interface ContactsMap {
  [contactId: string]: Contact;
}

type ContextValue = {
  contacts: ContactsMap;
  currentContact: Contact | undefined;
  addContact: (
    values: ContactValues
  ) => Promise<ApiResponse<Contact> | undefined>;
  addContacts: (
    contactValues: ContactValues[],
    labelValues: { name: string; color?: string }[]
  ) => Promise<Response[] | undefined>;
  updateContact: (
    contactId: string,
    values: ContactValues
  ) => Promise<ApiResponse<Contact>>;
  removeContact: (contactId: string) => Promise<ApiResponse<[]> | undefined>;
  removeContacts: (
    contactsIds: string[]
  ) => Promise<ApiResponse<[]> | undefined>;
  fetchContacts: () => Promise<ContactsMap>;
  updatePaymentMethod: (
    contactId: string,
    paymentMethodId: string,
    values: PaymentMethod
  ) => Promise<ApiResponse<PaymentMethod>>;
  removePaymentMethod: (
    contactId: string,
    paymentMethodId: string
  ) => Promise<ApiResponse<[]>>;
  getDefaultPaymentMethod: (
    contactId: string,
    currency: CurrencyKey
  ) => PaymentMethod | undefined;
  addPaymentMethod: (
    paymentMethod: PaymentMethod,
    contact: Contact
  ) => Promise<void>;
  addContactPayment: (
    values: ContactPaymentValues
  ) => Promise<ApiResponse<ContactPayment> | undefined>;
  addContactPayments: (
    commissionReportUuid: string,
    contactPaymentValues: {
      contact_uuid: string;
      currency: CurrencyKey;
      amount: number;
      context: ContactPaymentContextKey;
    }[]
  ) => Promise<ApiResponse<ContactPayment[]> | undefined>;
  removeContactPayment: (
    contactPayment: ContactPayment
  ) => Promise<ApiResponse<[]>>;
};

const ContactsContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

export const ContactsProvider: React.FC<Props> = (props) => {
  const { mutate } = useSWRConfig();
  const { currentWorkspaceId, setWorkspaces } = useWorkspaces();
  const { addLoader, removeLoader } = useLoading();
  const { fetchLabels } = useLabels();
  const { addToast } = useToast();
  const { setComments } = useComments();
  const [contacts, setContacts] = useState<ContactsMap>({});
  const { pathname } = useLocation();
  const [currentContactUuid, setCurrentContactUuid] = useState<string>("");

  const fetchContactDetails = useCallback(
    async (contactUuid: string): Promise<Contact | undefined> => {
      if (!contactUuid || !currentWorkspaceId) return;

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/contacts/${contactUuid}`
        );

        const apiResponse: ApiResponse<Contact> = await response.json();

        if (apiResponse.status) {
          setContacts((prevState) =>
            produce(prevState, (draft) => {
              draft[contactUuid] = apiResponse.data;
            })
          );
          setComments((prevState) =>
            produce(prevState, (draft) => {
              draft[contactUuid] = apiResponse.data.comments;
            })
          );
          return apiResponse.data;
        }
      } catch (e) {
        console.error(e);
        return;
      }
    },
    [currentWorkspaceId, setComments]
  );

  const { data: currentContactData } = useSWR<Contact | undefined>(
    currentWorkspaceId && currentContactUuid ? currentContactUuid : null,
    fetchContactDetails,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenHidden: false
    }
  );

  const currentContact = useMemo(
    () =>
      !currentContactUuid || currentContactData?.uuid !== currentContactUuid
        ? undefined
        : currentContactData,
    [currentContactData, currentContactUuid]
  );

  const contactDetailsPageMatch = matchPath<{
    id: string;
    section: string;
  }>(pathname, {
    path: `/contacts/:id/:section`
  });

  useEffect(() => {
    const newContactUuid = contactDetailsPageMatch?.params?.id || "";

    if (newContactUuid !== currentContactUuid) {
      setCurrentContactUuid(newContactUuid);
    }
  }, [contactDetailsPageMatch?.params?.id, currentContactUuid]);

  const fetchContacts = useCallback(async () => {
    if (!currentWorkspaceId) return {};

    addLoader(LoadingKey.workspaceContacts);

    const res = await fetchApi(`/api/workspace/${currentWorkspaceId}/contacts`);

    const apiResponse: ApiResponse<Contact[]> = await res.json();

    let newContacts = {};

    if (apiResponse.status) {
      newContacts = keyBy(apiResponse.data, "uuid");

      setContacts(newContacts);
    }

    removeLoader(LoadingKey.workspaceContacts);

    return newContacts;
  }, [addLoader, currentWorkspaceId, removeLoader]);

  const prevWorkspaceId = usePrevious(currentWorkspaceId);
  useEffect(() => {
    if (
      currentWorkspaceId &&
      prevWorkspaceId &&
      currentWorkspaceId !== prevWorkspaceId
    ) {
      setContacts({});
    }
  }, [currentWorkspaceId, prevWorkspaceId]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const addContact = useCallback(
    async (values: ContactValues) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/contacts`,
        {
          method: "POST",
          body: JSON.stringify(values)
        }
      );

      const apiResponse: ApiResponse<Contact> = await response.json();

      if (apiResponse) {
        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.contact += 1;
          })
        );

        await fetchContacts();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchContacts, setWorkspaces]
  );

  const addContacts = useCallback(
    async (
      contactValues: ContactValues[],
      labelValues: { name: string; color?: string }[]
    ) => {
      if (!currentWorkspaceId) return;

      const contactsChunks = chunk(contactValues, 500);

      const requestPromises = contactsChunks.map((chunkedContactValues) =>
        fetchApi(`/api/workspace/${currentWorkspaceId}/contacts/bulk`, {
          method: "POST",
          body: JSON.stringify({
            contacts: chunkedContactValues,
            labels: labelValues
          })
        })
      );

      const apiResponse: Response[] = await Promise.all(requestPromises);

      if (apiResponse) {
        await Promise.all([fetchContacts(), fetchLabels()]);

        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.contact = values(contacts).length;
          })
        );
      }

      return apiResponse;
    },
    [contacts, currentWorkspaceId, fetchContacts, fetchLabels, setWorkspaces]
  );

  const updateContact = useCallback(
    async (contactId: string, values: ContactValues) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/contacts/${contactId}`,
        {
          method: "PUT",
          body: JSON.stringify(values)
        }
      );

      const apiResponse: ApiResponse<Contact> = await response.json();

      mutate(contactId);

      await fetchContacts();

      return apiResponse;
    },
    [currentWorkspaceId, fetchContacts, mutate]
  );

  const removeContact = useCallback(
    async (contactId: string) => {
      if (!currentWorkspaceId) return;

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/contacts/${contactId}`,
          {
            method: "DELETE"
          }
        );

        const apiResponse: ApiResponse<[]> = await response.json();

        if (apiResponse.status) {
          setWorkspaces((prevState) =>
            produce(prevState, (draft) => {
              if (!draft[currentWorkspaceId].counts) return;

              draft[currentWorkspaceId].counts.contact -= 1;
            })
          );

          await fetchContacts();
        }

        if (!apiResponse.status) {
          addToast({
            type: "error",
            message: apiResponse.message || "Something went wrong"
          });
        }

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [addToast, currentWorkspaceId, fetchContacts, setWorkspaces]
  );

  const removeContacts = useCallback(
    async (contactsIds: string[]) => {
      if (!currentWorkspaceId) return;

      setContacts((prevContacts) => omit(prevContacts, contactsIds));

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/bulk/contacts`,
        {
          method: "DELETE",
          body: JSON.stringify({ contacts: contactsIds })
        }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      if (apiResponse.status) {
        await fetchContacts();

        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.contact = values(contacts).length;
          })
        );
      }

      if (!apiResponse.status) {
        addToast({
          type: "error",
          message: apiResponse.message || "Something went wrong"
        });
      }

      return apiResponse;
    },
    [addToast, contacts, currentWorkspaceId, fetchContacts, setWorkspaces]
  );

  const updatePaymentMethod = useCallback(
    async (
      contactId: string,
      paymentMethodId: string,
      values: PaymentMethod
    ) => {
      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/contact/${contactId}/payment-methods/${paymentMethodId}`,
          {
            method: "PUT",
            body: JSON.stringify(values)
          }
        );

        const apiResponse: ApiResponse<PaymentMethod> = await response.json();

        if (!apiResponse.status) return apiResponse;

        mutate(contactId);

        fetchContacts();

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [currentWorkspaceId, fetchContacts, mutate]
  );

  const removePaymentMethod = useCallback(
    async (contactId: string, paymentMethodId: string) => {
      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/contact/${contactId}/payment-methods/${paymentMethodId}`,
          {
            method: "DELETE"
          }
        );

        const apiResponse: ApiResponse<[]> = await response.json();

        if (!apiResponse.status) return apiResponse;

        mutate(contactId);

        fetchContacts();

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [currentWorkspaceId, fetchContacts, mutate]
  );

  const getDefaultPaymentMethod = useCallback(
    (contactId: string, currency: CurrencyKey) =>
      find(get(contacts, [contactId, "payment_methods"], []), {
        primary: true,
        currency
      }),
    [contacts]
  );

  const addPaymentMethod = useCallback(
    async (paymentMethod: PaymentMethod, contact: Contact) => {
      const {
        uuid,
        name,
        email,
        phone,
        payment_methods,
        linked_location_ids,
        vat
      } = contact;

      let newPaymentMethods = payment_methods;
      const currentDefaultMethod = payment_methods.filter(
        ({ primary, currency }) =>
          primary === true && currency === paymentMethod.currency
      )[0];

      if (paymentMethod.primary && currentDefaultMethod) {
        newPaymentMethods = payment_methods.map((method) => {
          if (method.uuid === currentDefaultMethod.uuid) {
            return {
              ...method,
              primary: false
            };
          }

          return method;
        });
      }

      if (!currentDefaultMethod) paymentMethod.primary = true;

      const contactToSave = {
        name,
        email,
        phone,
        payment_methods: [...newPaymentMethods, paymentMethod],
        locations_ids: linked_location_ids,
        vat
      };

      await updateContact(uuid, contactToSave);
    },
    [updateContact]
  );

  const addContactPayment = useCallback(
    async (values: ContactPaymentValues) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/contact-payment`,
        {
          method: "POST",
          body: JSON.stringify(values)
        }
      );

      const apiResponse: ApiResponse<ContactPayment> = await response.json();

      if (apiResponse.status) {
        mutate(values.contact_uuid);

        setContacts((prevState) =>
          produce(prevState, (draft) => {
            if (draft[values.contact_uuid]) {
              draft[values.contact_uuid].contact_payments.push(
                apiResponse.data
              );
            }
          })
        );
      }

      return apiResponse;
    },
    [currentWorkspaceId, mutate]
  );

  const addContactPayments = useCallback(
    async (
      commissionReportUuid: string,
      contactPaymentValues: {
        contact_uuid: string;
        currency: CurrencyKey;
        amount: number;
        context: ContactPaymentContextKey;
      }[]
    ) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/contact-payment/bulk`,
        {
          method: "POST",
          body: JSON.stringify({
            report_commission_uuid: commissionReportUuid,
            contacts: contactPaymentValues
          })
        }
      );

      const apiResponse: ApiResponse<ContactPayment[]> = await response.json();

      if (apiResponse.status) {
        setContacts((prevState) =>
          produce(prevState, (draft) => {
            apiResponse.data.forEach((contactPayment) => {
              if (draft[contactPayment.contact_uuid]) {
                draft[contactPayment.contact_uuid].contact_payments.push(
                  contactPayment
                );
              }
            });
          })
        );
      }

      return apiResponse;
    },
    [currentWorkspaceId]
  );

  const removeContactPayment = useCallback(
    async (contactPayment: ContactPayment) => {
      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/contact-payment/${contactPayment.uuid}`,
          {
            method: "DELETE"
          }
        );

        const apiResponse: ApiResponse<[]> = await response.json();

        if (!apiResponse.status) return apiResponse;

        mutate(contactPayment.contact_uuid);

        setContacts((prevState) =>
          produce(prevState, (draft) => {
            if (draft[contactPayment.contact_uuid]) {
              draft[contactPayment.contact_uuid].contact_payments = draft[
                contactPayment.contact_uuid
              ].contact_payments.filter(
                ({ uuid }) => uuid === contactPayment.uuid
              );
            }
          })
        );

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [currentWorkspaceId, mutate]
  );

  const value = useMemo(
    () => ({
      contacts,
      currentContact,
      fetchContacts,
      addContact,
      addContacts,
      updateContact,
      removeContact,
      removeContacts,
      updatePaymentMethod,
      removePaymentMethod,
      getDefaultPaymentMethod,
      addPaymentMethod,
      addContactPayment,
      addContactPayments,
      removeContactPayment
    }),
    [
      contacts,
      currentContact,
      fetchContacts,
      addContact,
      addContacts,
      updateContact,
      removeContact,
      removeContacts,
      updatePaymentMethod,
      removePaymentMethod,
      getDefaultPaymentMethod,
      addPaymentMethod,
      addContactPayment,
      addContactPayments,
      removeContactPayment
    ]
  );

  return <ContactsContext.Provider value={value} {...props} />;
};

export const useContacts = (): ContextValue => {
  const context = useContext(ContactsContext);
  if (context === undefined) {
    throw new Error("useContacts must be used within an ContactsProvider");
  }
  return context;
};

//
// Utils
//

export interface ContactValues {
  name: string;
  email: string | null;
  phone: string | null;
  vat: string | null;
  payment_methods: PaymentMethod[] | null;
}

export interface ContactPaymentValues {
  currency: CurrencyKey;
  amount: number;
  workspace_uuid: string;
  contact_uuid: string;
  report_commission_uuid: string | null;
  notes: string | null;
  context: ContactPaymentContextKey;
  paid_at: string;
}

export enum ContactPaymentContextKey {
  manual = "manual",
  csv = "csv",
  qr = "qr",
  cli = "cli",
  wiseByEmail = "wiseByEmail",
  paypal = "paypal",
  wallet = "wallet"
}

export const getPaymentContextTitle = (context: ContactPaymentContextKey) => {
  switch (context) {
    case ContactPaymentContextKey.manual:
      return "Manual";
    case ContactPaymentContextKey.csv:
      return "CSV";
    case ContactPaymentContextKey.qr:
      return "QR";
    case ContactPaymentContextKey.cli:
      return "CLI";
    case ContactPaymentContextKey.wiseByEmail:
      return "Wise.com csv";
    case ContactPaymentContextKey.paypal:
      return "PayPal csv";
    case ContactPaymentContextKey.wallet:
        return "Helium Wallet";
    default:
      return context;
  }
};
